import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import WagerPanel from "@/components/WagerPanel";
import { toast } from "sonner";
import { Grid3x3, RotateCcw } from "lucide-react";
import { maybeCelebrate } from "@/lib/celebrate";
import { playSfx } from "@/lib/sfx";

const SIZE = 4;
const TARGET = 2048;

function newBoard() {
  const b = Array.from({ length: SIZE }, () => Array(SIZE).fill(0));
  addRandom(b); addRandom(b);
  return b;
}
function addRandom(b) {
  const empty = [];
  for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) if (b[r][c] === 0) empty.push([r, c]);
  if (!empty.length) return false;
  const [r, c] = empty[Math.floor(Math.random() * empty.length)];
  b[r][c] = Math.random() < 0.9 ? 2 : 4;
  return true;
}
function slideRowLeft(row) {
  const arr = row.filter((v) => v);
  let gained = 0;
  for (let i = 0; i < arr.length - 1; i++) {
    if (arr[i] === arr[i + 1]) { arr[i] *= 2; gained += arr[i]; arr[i + 1] = 0; }
  }
  const cleaned = arr.filter((v) => v);
  while (cleaned.length < SIZE) cleaned.push(0);
  return { row: cleaned, gained };
}
function rotate(b) { // rotate CW
  const n = b.length; const r = Array.from({ length: n }, () => Array(n).fill(0));
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) r[j][n - 1 - i] = b[i][j];
  return r;
}
function move(board, dir) {
  let b = board.map((r) => [...r]);
  const rot = { left: 0, up: 3, right: 2, down: 1 }[dir];
  for (let i = 0; i < rot; i++) b = rotate(b);
  let gained = 0, changed = false;
  for (let r = 0; r < SIZE; r++) {
    const before = b[r].join(",");
    const { row, gained: g } = slideRowLeft(b[r]);
    b[r] = row; gained += g;
    if (row.join(",") !== before) changed = true;
  }
  for (let i = 0; i < (4 - rot) % 4; i++) b = rotate(b);
  return { board: b, gained, changed };
}
function bestTile(b) { let m = 0; b.forEach((r) => r.forEach((v) => { if (v > m) m = v; })); return m; }
function canMove(b) {
  for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) {
    if (b[r][c] === 0) return true;
    if (c + 1 < SIZE && b[r][c] === b[r][c + 1]) return true;
    if (r + 1 < SIZE && b[r][c] === b[r + 1][c]) return true;
  }
  return false;
}

export default function TwentyFortyEight() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [board, setBoard] = useState(newBoard());
  const [score, setScore] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [ended, setEnded] = useState(false);

  const start = async (wager) => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/wager", { game: "2048", wager });
      setSession(data);
      setBoard(newBoard());
      setScore(0);
      setResult(null);
      setEnded(false);
      playSfx("wagerConfirm");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot start"); }
    finally { setBusy(false); }
  };

  const settle = useCallback(async (finalBoard) => {
    if (!session || result) return;
    setBusy(true);
    try {
      const { data } = await api.post("/games/settle", {
        session_id: session.session_id,
        outcome: { best_tile: bestTile(finalBoard), target: TARGET, score },
      });
      setResult(data);
      if (data.payout > 0) {
        toast.success(`+${data.payout.toLocaleString()} chips`);
        maybeCelebrate({ payout: data.payout, wager: session.wager });
        playSfx("cashOut");
        if (data.payout >= session.wager * 3) setTimeout(() => playSfx("megaWin"), 200);
      }
      else { toast.error(`No payout · lost ${session.wager}`); playSfx("lose"); }
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Settle failed"); }
    finally { setBusy(false); }
  }, [session, result, score, refresh]);

  const doMove = useCallback((dir) => {
    if (!session || result || ended) return;
    const { board: nb, gained, changed } = move(board, dir);
    if (!changed) return;
    addRandom(nb);
    setBoard(nb);
    setScore((s) => s + gained);
    if (gained > 0) playSfx("match"); else playSfx("step");
    if (bestTile(nb) >= TARGET) { setEnded(true); settle(nb); }
    else if (!canMove(nb)) { setEnded(true); settle(nb); }
  }, [board, session, result, ended, settle]);

  useEffect(() => {
    const onKey = (e) => {
      const map = { ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down", a: "left", d: "right", w: "up", s: "down" };
      const dir = map[e.key];
      if (dir) { e.preventDefault(); doMove(dir); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doMove]);

  // touch swipe
  useEffect(() => {
    let sx = 0, sy = 0;
    const ts = (e) => { const t = e.touches[0]; sx = t.clientX; sy = t.clientY; };
    const te = (e) => {
      const t = e.changedTouches[0]; const dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) < 20 && Math.abs(dy) < 20) return;
      if (Math.abs(dx) > Math.abs(dy)) doMove(dx > 0 ? "right" : "left");
      else doMove(dy > 0 ? "down" : "up");
    };
    const el = document.getElementById("board-2048");
    if (el) { el.addEventListener("touchstart", ts); el.addEventListener("touchend", te); }
    return () => { if (el) { el.removeEventListener("touchstart", ts); el.removeEventListener("touchend", te); } };
  }, [doMove]);

  const restart = () => { setSession(null); setResult(null); setEnded(false); };

  return (
    <div className="space-y-6" data-testid="p2048-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="pill pill-gold inline-flex items-center gap-1"><Grid3x3 className="w-3.5 h-3.5" /> 2048 Vault</div>
          <h1 className="font-display text-3xl mt-2">Merge to the Vault</h1>
        </div>
        <button onClick={() => nav("/lobby")} className="btn-ghost text-sm" data-testid="back-to-lobby">Back to lobby</button>
      </div>

      {!session ? (
        <WagerPanel game="2048" onStart={start} busy={busy} />
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
          <div id="board-2048" data-testid="p2048-board" className={`vexis-card p-4 sm:p-6 select-none ${result ? (result.payout > 0 ? "win-glow" : "loss-glow") : ""}`}>
            <div className="grid grid-cols-4 gap-2 sm:gap-3">
              {board.flat().map((v, i) => (
                <div key={i} className={`tile aspect-square text-lg sm:text-2xl ${v ? `t-${v}` : "bg-[#0E1218] border border-white/5"}`} data-testid={`p2048-tile-${i}`}>
                  {v || ""}
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-slate-500 font-mono text-center">Arrow keys · WASD · swipe on mobile</div>
            <div className="grid grid-cols-3 gap-2 mt-4 sm:hidden">
              <div />
              <button className="btn-ghost text-sm" onClick={() => doMove("up")} data-testid="p2048-up">↑</button>
              <div />
              <button className="btn-ghost text-sm" onClick={() => doMove("left")} data-testid="p2048-left">←</button>
              <button className="btn-ghost text-sm" onClick={() => doMove("down")} data-testid="p2048-down">↓</button>
              <button className="btn-ghost text-sm" onClick={() => doMove("right")} data-testid="p2048-right">→</button>
            </div>
          </div>

          <div className="space-y-3">
            <div className="vexis-card p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-slate-500 text-xs font-mono uppercase">Best tile</div><div className="font-display text-xl">{bestTile(board)}</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Score</div><div className="font-display text-xl">{score}</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Wager</div><div className="font-display text-xl">{session.wager}</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Target</div><div className="font-display text-xl text-[color:var(--gold)]">2048 · 6×</div></div>
              </div>
              <div className="mt-3 text-xs text-slate-400">128 · 1.2×, 256 · 1.8×, 512 · 2.5×, 1024 · 3.5×, 2048 · 6×</div>
            </div>
            {!result && (
              <button className="btn-ghost w-full" onClick={() => { setEnded(true); settle(board); }} data-testid="p2048-stand">Stand & Settle</button>
            )}
            {result && (
              <div className={`vexis-card p-4 ${result.payout > 0 ? "win-glow" : "loss-glow"}`} data-testid="p2048-result">
                {result.payout >= session.wager * 2 && (
                  <div className="pill pill-gold mb-2 inline-flex" data-testid="p2048-mega">✦ Mega Win · {(result.payout / session.wager).toFixed(2)}×</div>
                )}
                <div className={`font-display text-2xl ${result.payout > 0 ? "text-emerald-400" : "text-red-400"}`}>{result.payout > 0 ? "Payout" : "No payout"}</div>
                <div className="text-sm mt-1 text-slate-300">+<span className="font-mono">{result.payout}</span> chips</div>
                <button onClick={restart} className="btn-gold w-full mt-4 inline-flex justify-center items-center gap-2" data-testid="p2048-play-again"><RotateCcw className="w-4 h-4" /> Play again</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
